import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XIcon,
  AlertTriangleIcon,
  InfoIcon,
  BellIcon,
  ShieldCheckIcon,
  Trash2Icon } from
'lucide-react';
import { useAlarms, AgentMessage } from '../../hooks/useAlarms';
import { useTranslation } from 'react-i18next';

interface AlarmsSheetProps {
  onClose: () => void;
  apartmentAssetId?: string | null;
}

export function AlarmsSheet({ onClose, apartmentAssetId = null }: AlarmsSheetProps) {
  const { t } = useTranslation();
  const { messages, loading, acknowledge, clear } = useAlarms(apartmentAssetId);

  const handleCardClick = (msg: AgentMessage) => {
    if (!msg.isRead) {
      acknowledge(msg.id);
    }
  };

  const handleClear = (e: React.MouseEvent, msgId: string) => {
    e.stopPropagation();
    clear(msgId);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[100] flex items-end justify-center"
      onClick={onClose}>
      
      {/* Backdrop */}
      <div className="absolute inset-0 backdrop-blur-sm" style={{ backgroundColor: 'var(--mobile-bg-overlay)' }} />

      {/* Sheet */}
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 35
        }}
        onClick={(e) => e.stopPropagation()}
        className="relative z-10 w-full max-w-md backdrop-blur-xl border-t rounded-t-2xl overflow-hidden flex flex-col max-h-[85vh]"
        style={{ backgroundColor: 'var(--mobile-bg-sheet)', borderColor: 'var(--mobile-border-strong)' }}>
        
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full" style={{ background: 'var(--mobile-border-strong)' }} />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b flex-shrink-0" style={{ borderColor: 'var(--mobile-border)' }}>
          <div className="flex items-center gap-2.5">
            <div className={`p-1.5 rounded-lg ${messages.length > 0 ? 'bg-red-500/20' : 'bg-slate-500/20'}`}>
              <BellIcon className={`w-4 h-4 ${messages.length > 0 ? 'text-red-400' : 'text-slate-400'}`} />
            </div>
            <div>
              <h2 className="text-[1rem] font-medium font-bold" style={{ color: 'var(--mobile-text-primary)' }}>
                SED 집사
              </h2>
              <p className="text-[0.8rem] font-mono" style={{ color: 'var(--mobile-text-muted)' }}>
                시스템 알람 및 정보
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg active:opacity-60"
            style={{ color: 'var(--mobile-text-muted)' }}
            aria-label="닫기">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-3 overflow-y-auto min-h-0 flex-1 custom-scrollbar pb-10">
          {loading && messages.length === 0 ? (
            <div className="flex flex-col items-center py-10 gap-3">
               <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-6 h-6 border-2 border-cyan-500/20 border-t-cyan-500 rounded-full"
              />
              <span className="text-[0.8rem] font-medium" style={{ color: 'var(--mobile-text-muted)' }}>알람을 불러오는 중...</span>
            </div>
          ) : messages.length > 0 ? (
            <div className="space-y-2">
              <AnimatePresence mode="popLayout">
                {messages.map((msg) => {
                  const isWarning = msg.type === 'warning';
                  return (
                    <motion.div
                      key={msg.id}
                      layout
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      onClick={() => handleCardClick(msg)}
                      className={`
                        relative flex items-start gap-3 p-3.5 rounded-xl border transition-all duration-300 cursor-pointer
                        ${isWarning ? 'bg-amber-500/10 border-amber-500/20' : ''}
                        ${!msg.isRead ? 'ring-1 ring-cyan-500/30 shadow-[0_0_12px_rgba(34,211,238,0.1)]' : 'opacity-80'}
                      `}
                      style={!isWarning ? { backgroundColor: 'var(--mobile-bg-sub)', borderColor: 'var(--mobile-border)' } : {}}>
                      
                      {/* Left accent bar for unread */}
                      {!msg.isRead && (
                        <div className="absolute left-0 top-3 bottom-3 w-1 bg-cyan-500 rounded-r-full" />
                      )}

                      {/* Icon */}
                      <div className={`p-1.5 rounded-lg flex-shrink-0 ${isWarning ? 'bg-amber-500/20 text-amber-400' : 'bg-cyan-500/15 text-cyan-400'}`}>
                        {isWarning ? <AlertTriangleIcon className="w-4 h-4" /> : <InfoIcon className="w-4 h-4" />}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0 pr-6">
                        <div className={`text-[0.9rem] font-medium font-bold mb-0.5 ${isWarning ? 'text-amber-200' : ''}`}
                          style={!isWarning ? { color: 'var(--mobile-text-primary)' } : {}}>
                          {msg.title}
                        </div>
                        <div className="text-[0.8rem] font-medium leading-relaxed line-clamp-2" style={{ color: 'var(--mobile-text-secondary)' }}>
                          {msg.description}
                        </div>
                        <div className="mt-1.5 text-[0.8rem] font-medium font-mono" style={{ color: 'var(--mobile-text-muted)' }}>
                          {new Intl.DateTimeFormat('ko-KR', { hour: 'numeric', minute: 'numeric', day: 'numeric', month: 'short' }).format(new Date(msg.createdTime))}
                        </div>
                      </div>

                      {/* Delete Button */}
                      <button
                        onClick={(e) => handleClear(e, msg.id)}
                        className="absolute right-3 top-3 p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        title={t('common.delete')}>
                        <Trash2Icon className="w-4 h-4" />
                      </button>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          ) : (
            <div className="flex flex-col items-center py-12 gap-3">
              <div className="w-16 h-16 rounded-full bg-emerald-500/5 flex items-center justify-center border border-emerald-500/10">
                <ShieldCheckIcon className="w-8 h-8 text-emerald-500/40" />
              </div>
              <div className="text-center">
                <span className="block text-sm font-bold" style={{ color: 'var(--mobile-text-secondary)' }}>활성화된 알람이 없습니다</span>
                <span className="text-[10px] mt-1" style={{ color: 'var(--mobile-text-muted)' }}>모든 시스템이 정상적으로 동작 중입니다</span>
              </div>
            </div>
          )}
        </div>

        {/* Bottom safe area */}
        <div className="h-6 flex-shrink-0" />
      </motion.div>
    </motion.div>
  );
}



